import { createRef } from 'react';
import {IContentBlock, IDesignBlock} from '../../types/iblock';
import { IHeroSliderBlockData } from '../../types/content-block-data';
import { escapeHTML } from '../../lib/page-builder-lib';
import blocksManager from '../../stores/blocks-manager';
import pageBuilderStore from '../../stores/pageBuilderStore';
import contentManager from '../../stores/content-manager';

function getRootStyles(data: IHeroSliderBlockData) {
  return [
    "position:relative;",
    `width:${data.width?.value ? data.width.value + (data.width.unit || "px") : "100%"};`,
    `height:${data.height?.value ? data.height.value + (data.height.unit || "px") : "auto"};`,
    `border-radius:${data.borderRadius?.value ? data.borderRadius.value + (data.borderRadius.unit || "12px") : "12px"};`,
    `background:${data.backgroundColor || "#f8fafc"};`,
    "overflow:hidden;",
    "display:flex;",
    "flex-direction:column;",
    "justify-content:center;",
    "padding:1rem;",              // p-4
  ].join(" ");
}

function getAbsoluteFillStyles(extra: string = "") {
  // For .relative w-full h-full flex-1 overflow-hidden rounded-lg
  return [
    "position:relative;",
    "width:100%;",
    "height:100%;",
    "flex:1 1 0%;",
    "overflow:hidden;",
    "border-radius:0.5rem;", // rounded-lg ~ 8px
    extra,
  ].join("");
}

/**
 * Serializes a HeroSliderBlock content block into static HTML markup.
 *
 * - Handles both Hero mode (with header, text, CTA button) and Carousel mode (images with optional links/captions).
 * - Adds unique selectors as CSS classes for block, header, text, and CTA (for style targeting).
 * - In Hero mode, renders each slide's background image, overlay color, hero header, hero text, and CTA.
 * - In Carousel mode, renders each slide as an image (optionally wrapped in a link) and a caption.
 * - All images use alt attributes; CTA links use target/rel for new tab when needed.
 * - Returns a semantic, accessible HTML string suitable for preview/export.
 *
 * @param block The IContentBlock instance (must be of blockType "hero_slider").
 * @returns HTML markup string for the HeroSliderBlock.
 *
 * @example
 * const html = fromHeroSliderBlockToHTML(block);
 * // -> <div class="d2d-hero-slider-block">...</div>
 *
 * @remarks
 * - If the HeroSliderBlock contains no slides, outputs a simple empty state.
 * - The output HTML is static (no interactive slider functionality) and expects styling via serialized CSS.
 * - Expects selectors for sub-elements (heroHeader, heroText, cta) to be set in block data.
 */
export function fromHeroSliderBlockToHTML(block: IContentBlock): string {
  if (block.blockType !== "hero_slider") return "";
  const heroSliderData = block.blockData as IHeroSliderBlockData;
  const isPreview = pageBuilderStore.previewMode?.showPreview;
  const slidesToRender = isPreview ? [heroSliderData.slides[0]] : heroSliderData.slides;

  if (!heroSliderData.slides || heroSliderData.slides.length === 0) {
    return `<div class="${block.selector || 'd2d-hero-slider-block'}">No images in carousel.</div>`;
  }

  // Root container
  let html = isPreview
    ? `<div class="${block.selector || 'd2d-hero-slider-block'}" style="${getRootStyles(heroSliderData)}">`
    : `<div class="${block.selector || 'd2d-hero-slider-block'}">`;

  for (const slide of slidesToRender) {
    if (slide.isHeroMode) {
      html += isPreview
      ? `<div style="position:relative;width:100%;height:100%;flex:1 1 0%;overflow:hidden;border-radius:0.5rem;">`
      : `<div class="${slide.heroHeader?.selector || ""} relative w-full h-full flex-1 overflow-hidden rounded-lg">`;

      
      // BG image
      if (slide.slideImage?.src) {
        html += `<img src="${slide.slideImage?.src}" alt="${slide.slideImage?.alt || ''}" style="
          position:absolute; inset:0; width:100%; height:100%; object-fit:${slide.slideImage.imageFit || "cover"}; 
          object-position: ${slide.slideImage.imagePosition || "center"}; z-index:0; pointer-events:none;
          ${heroSliderData.borderRadius?.value ? `border-radius:${heroSliderData.borderRadius.value}${heroSliderData.borderRadius.unit || "12px"};` : ""}
        " draggable="false" />`;
      }

      // Overlay
      if (slide.overlayColor) {
        html += `<div style="
          background:${slide.overlayColor};
          position:absolute; inset:0; pointer-events:none;
          border-radius:${heroSliderData.borderRadius?.value ? heroSliderData.borderRadius.value + (heroSliderData.borderRadius.unit || "12px") : "12px"};
          z-index:1;
        "></div>`;
      }

      // Slide Content
      html += `<div style="
        position:absolute; inset:0;
        display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; padding-left:1.5rem; padding-right:1.5rem; z-index:20;
      ">`;

      // Hero Header
      if (slide.heroHeader?.text) {
        html += isPreview
          ? `<h3 style="
              color:${slide.heroHeader.color ?? "#fff"};
              font-family:${slide.heroHeader.fontFamily ?? "Montserrat, sans-serif"};
              font-size:${slide.heroHeader.fontSize?.value ? `${slide.heroHeader.fontSize.value}${slide.heroHeader.fontSize.unit || "rem"}` : "1.875rem"};
              line-height:${slide.heroHeader.lineHeight?.value ? `${slide.heroHeader.lineHeight.value}${slide.heroHeader.lineHeight.unit || ""}` : "1.2"};
              letter-spacing:${slide.heroHeader.letterSpacing?.value ? `${slide.heroHeader.letterSpacing.value}${slide.heroHeader.letterSpacing.unit || "px"}` : "0px"};
              margin:${blocksManager.dimensionToCss(slide.heroHeader.margin) ?? "0"};
              padding:${blocksManager.dimensionToCss(slide.heroHeader.padding) ?? "0"};
              font-weight:${slide.heroHeader.fontWeight ?? "700"};
              text-decoration:${slide.heroHeader.textDecoration ?? "none"};
              text-transform:${slide.heroHeader.textTransform ?? "none"};
              text-align:${slide.heroHeader.alignment ?? "center"};
              text-shadow:0 2px 12px rgba(0,0,0,0.24);
              transition:all 0.15s;
              margin-top:1.5rem;
          ">${slide.heroHeader.text}</h3>`
          : `<h3 class="${slide.heroHeader.selector || ""} text-center mt-6">${slide.heroHeader.text}</h3>`;
      }

      // Hero Text
      if (slide.heroText?.text) {
        html += isPreview
          ? `<p style="
              color:${slide.heroText.color ?? "#fff"};
              font-family:${slide.heroText.fontFamily ?? "Inter, sans-serif"};
              font-size:${slide.heroText.fontSize?.value ? `${slide.heroText.fontSize.value}${slide.heroText.fontSize.unit || "rem"}` : "1rem"};
              line-height:${slide.heroText.lineHeight?.value ? `${slide.heroText.lineHeight.value}${slide.heroText.lineHeight.unit || ""}` : "1.5rem"};
              letter-spacing:${slide.heroText.letterSpacing?.value ? `${slide.heroText.letterSpacing.value}${slide.heroText.letterSpacing.unit || "px"}` : "0px"};
              margin:${blocksManager.dimensionToCss(slide.heroText.margin) ?? "0"};
              padding:${blocksManager.dimensionToCss(slide.heroText.padding) ?? "0"};
              font-weight:${slide.heroText.fontWeight ?? "400"};
              text-decoration:${slide.heroText.textDecoration ?? "none"};
              text-transform:${slide.heroText.textTransform ?? "none"};
              text-align:${slide.heroText.alignment ?? "center"};
              text-shadow:0 1px 10px rgba(0,0,0,0.13);
              transition:all 0.15s;
          ">${slide.heroText.text}</p>`
          : `<p class="${slide.heroText.selector || ""} text-center">${slide.heroText.text}</p>`;
      }

      // CTA Button
      if (slide.cta?.text) {
        html += isPreview
          ? `<div style="
                display:flex; margin-top:1rem; justify-content:${slide.cta.alignment === "left" ? "flex-start" : slide.cta.alignment === "right" ? "flex-end" : "center"};
            ">
              <a href="${slide.cta.url ?? "#"}"
                style="padding:0.75rem 1rem; border-radius:0.375rem; background:#2563eb; color:white; font-weight:600; transition:all 0.15s; text-align:${slide.cta.alignment ?? "center"};"
                target="${slide.cta.openInNewTab ? "_blank" : "_self"}" rel="noopener noreferrer"
              >${slide.cta.text}</a>
            </div>`
          : `<div class="flex mt-4 ${slide.cta.alignment === "left" ? "justify-start" : slide.cta.alignment === "right" ? "justify-end" : "justify-center"}">
              <a href="${slide.cta.url ?? "#"}" class="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 font-semibold transition"
                target="${slide.cta.openInNewTab ? "_blank" : "_self"}" rel="noopener noreferrer"
              >${slide.cta.text}</a>
            </div>`;
      }

      html += `</div></div>`;
    } else {
      // CAROUSEL MODE
      html += isPreview
        ? `<div style="${getAbsoluteFillStyles()}">`
        : `<div class="d2d-hero-slide relative w-full h-full flex-1 overflow-hidden rounded-lg">`;

      let imgTag = `<img src="${slide.slideImage?.src || ''}" alt="${slide.slideImage?.alt || ''}"
        style="width:100%;height:100%;object-fit:${slide.slideImage.imageFit || "cover"}; 
          object-position: ${slide.slideImage.imagePosition || "center"}; display:block;" draggable="false" />`;

      if (slide.slideImage?.url) {
        imgTag = `
          <a href="${slide.slideImage.url}" ${slide.slideImage.openInNewTab ? 'target="_blank" rel="noopener noreferrer"' : ''}>
            ${imgTag}
          </a>`;
      }

      html += imgTag;

      if (slide.slideImage?.caption) {
        html += `<div style="
          position:absolute; bottom:1rem; left:0; right:0; padding-left:1rem; padding-right:1rem; z-index:20;
          background:rgba(0,0,0,0.4); border-radius:0.5rem; color:white; font-size:1rem; font-weight:600; text-align:center;
          pointer-events:none; margin:0 auto; max-width:80%;
        ">${slide.slideImage.caption}</div>`;
      }

      html += `</div>`;
    }
  }

  html += `</div>`;
  return html;
}

/**
 * Serializes a HeroSliderBlock content block into a clean, minimal JSON object
 * suitable for storage, export, or re-import.
 *
 * - Only includes the essential configuration and text fields needed to reconstruct the block.
 * - Serializes each slide's overlay color, hero header, hero text, CTA, and slide image fields.
 * - For sub-objects (heroHeader, heroText, cta), only selector and text/link values are included.
 * - Excludes all runtime, UI, or transient fields for a safe export.
 *
 * @param block The IContentBlock instance (must be of blockType "hero_slider").
 * @returns JSON-ready plain object representing the block's configuration and content.
 *
 * @example
 * const json = fromHeroSliderBlockToJSON(block);
 * // -> {
 * //      contentBlockId: "...",
 * //      blockType: "hero_slider",
 * //      blockContent: { ... }
 * //    }
 *
 * @remarks
 * - overlayColor is included as a field, since it is not a style object but a per-slide setting.
 * - This function should be updated if new fields are added to the HeroSlider data model.
 */
export function fromHeroSliderBlockToJSON(block: IContentBlock): object {
  if (block.blockType !== "hero_slider") return {};

  const data = block.blockData as IHeroSliderBlockData;
  return {
    contentBlockId: block.contentBlockId,
    blockType: block.blockType,
    selector: block.selector,
    blockData: {
      showArrows: data.showArrows,
      showIndicators: data.showIndicators,
      autoplay: data.autoplay,
      autoplayInterval: data.autoplayInterval,
      loop: data.loop,
      slides: data.slides.map(slide => ({
        // Only serialize values you want to keep!
        isHeroMode: slide.isHeroMode,
        overlayColor: slide.overlayColor,
        // Slide image content (include only fields needed)
        slideImage: slide.slideImage
          ? {
              src: slide.slideImage.src,
              alt: slide.slideImage.alt,
              caption: slide.slideImage.caption,
              url: slide.slideImage.url,
              openInNewTab: slide.slideImage.openInNewTab,
            }
          : undefined,
        // Hero Header
        heroHeader: slide.heroHeader
          ? {
              selector: slide.heroHeader.selector,
              text: slide.heroHeader.text,
            }
          : undefined,
        // Hero Text
        heroText: slide.heroText
          ? {
              selector: slide.heroText.selector,
              text: slide.heroText.text,
            }
          : undefined,
        // CTA
        cta: slide.cta
          ? {
              selector: slide.cta.selector,
              text: slide.cta.text,
              url: slide.cta.url,
              openInNewTab: slide.cta.openInNewTab,
              // Add any new fields as needed
            }
          : undefined,
      })),
    },
  };
}

/**
 * Reconstructs a HeroSliderBlock (Carousel) from exported JSON data.
 *
 * @param json - The JSON object to parse (must match your export structure)
 * @returns An IDesignBlock instance for store/UI
 */
export function fromJSONToHeroSliderBlock(json: any, context: "canvas" | "columns" = "canvas"): IDesignBlock {
  const cssStyles = contentManager.getStylesForSelector(json.selector);
    
  const newDesignBlockRef = createRef<HTMLDivElement>();
  const newContentBlockRef = createRef<HTMLElement>(); // generic for now
  // Defensive: Support both new and legacy shapes
  const blockData = json.blockData || {};

  const block: IDesignBlock = {
    designBlockId: crypto.randomUUID(),
    designBlockRef: newDesignBlockRef,
    contentBlock: {
      contentBlockId: json.contentBlockId || crypto.randomUUID(),
      blockType: json.blockType || "hero_slider",
      contentBlockRef: newContentBlockRef,
      selector: json.selector,
      blockData: {
        showArrows: blockData.showArrows ?? true,
        showIndicators: blockData.showIndicators ?? true,
        autoplay: blockData.autoplay,
        autoplayInterval: blockData.autoplayInterval,
        loop: blockData.loop,
        width: blocksManager.parseDimensions(cssStyles?.["width"], "single"),
        height: blocksManager.parseDimensions(cssStyles?.["height"], "single"),
        borderRadius: blocksManager.parseDimensions(cssStyles?.["border-radius"], "four-corners"),
        slides: blockData.slides.map((slide: any) => ({
          isHeroMode: slide.isHeroMode ?? false,
          overlayColor: slide.overlayColor,
          slideImage: slide.slideImage
            ? {
                src: slide.slideImage.src ?? "",
                alt: slide.slideImage.alt ?? "",
                caption: slide.slideImage.caption ?? "",
                url: slide.slideImage.url ?? "",
                openInNewTab: slide.slideImage.openInNewTab ?? false,
                imageFit: contentManager.getStylesForSelector(slide.slideImage.selector)?.["object-fit"] ?? "cover",
                imagePosition: contentManager.getStylesForSelector(slide.slideImage.selector)?.["object-position"] ?? "center",
              }
            : undefined,
          heroHeader: slide.heroHeader
            ? {
                selector: slide.heroHeader.selector,
                text: slide.heroHeader.text ?? "",
                color: contentManager.getStylesForSelector(slide.heroHeader.selector)?.["color"] ?? "#ffffff",
                fontFamily: contentManager.getStylesForSelector(slide.heroHeader.selector)?.["font-family"] ?? "Montserrat, sans-serif",
                fontSize: blocksManager.parseDimensions(contentManager.getStylesForSelector(slide.heroHeader.selector)?.["font-size"], "single"),
                fontWeight: contentManager.getStylesForSelector(slide.heroHeader.selector)?.["font-weight"] ?? "700",
                lineHeight: blocksManager.parseDimensions(contentManager.getStylesForSelector(slide.heroHeader.selector)?.["line-height"], "single"),
                letterSpacing: blocksManager.parseDimensions(contentManager.getStylesForSelector(slide.heroHeader.selector)?.["letter-spacing"], "single"),
                margin: blocksManager.parseDimensions(contentManager.getStylesForSelector(slide.heroHeader.selector)?.["margin"], "four-side"),
                padding: blocksManager.parseDimensions(contentManager.getStylesForSelector(slide.heroHeader.selector)?.["padding"], "four-side"),
                textDecoration: contentManager.getStylesForSelector(slide.heroHeader.selector)?.["text-decoration"] ?? "none",
                textTransform: contentManager.getStylesForSelector(slide.heroHeader.selector)?.["text-transform"] ?? "none",
                alignment: contentManager.getStylesForSelector(slide.heroHeader.selector)?.["alignment"] ?? "left",
                // Add more heroHeader fields here as you support them (e.g., fontFamily, etc)
              }
            : undefined,
          heroText: slide.heroText
            ? {
                selector: slide.heroText.selector,
                text: slide.heroText.text ?? "",
                color: contentManager.getStylesForSelector(slide.heroText.selector)?.["color"] ?? "#ffffff",
                fontFamily: contentManager.getStylesForSelector(slide.heroText.selector)?.["font-family"] ?? "Montserrat, sans-serif",
                fontSize: blocksManager.parseDimensions(contentManager.getStylesForSelector(slide.heroText.selector)?.["font-size"], "single"),
                fontWeight: contentManager.getStylesForSelector(slide.heroText.selector)?.["font-weight"] ?? "700",
                lineHeight: blocksManager.parseDimensions(contentManager.getStylesForSelector(slide.heroText.selector)?.["line-height"], "single"),
                letterSpacing: blocksManager.parseDimensions(contentManager.getStylesForSelector(slide.heroText.selector)?.["letter-spacing"], "single"),
                margin: blocksManager.parseDimensions(contentManager.getStylesForSelector(slide.heroText.selector)?.["margin"], "four-side"),
                padding: blocksManager.parseDimensions(contentManager.getStylesForSelector(slide.heroText.selector)?.["padding"], "four-side"),
                textDecoration: contentManager.getStylesForSelector(slide.heroText.selector)?.["text-decoration"] ?? "none",
                textTransform: contentManager.getStylesForSelector(slide.heroText.selector)?.["text-transform"] ?? "none",
                alignment: contentManager.getStylesForSelector(slide.heroText.selector)?.["alignment"] ?? "left",
              }
            : undefined,
          cta: slide.cta
            ? {
                selector: slide.cta.selector,
                text: slide.cta.text ?? "",
                url: slide.cta.url ?? "",
                openInNewTab: slide.cta.openInNewTab ?? false,
                alignment: contentManager.getStylesForSelector(slide.cta.selector)?.["alignment"] ?? "left",
                // Add more cta fields here as needed
              }
            : undefined,
        })),
      } as IHeroSliderBlockData,
    } as IContentBlock,
  };

  blocksManager.addDesignTreeEntry({
		contentBlockId: block.contentBlock.contentBlockId,
		designBlockId: block.designBlockId,
		designBlockRef: newDesignBlockRef,
		contentBlockRef: newContentBlockRef,
		contentBlockType: block.contentBlock.blockType.toUpperCase(),
		location: context,
	});

  return block;
}